import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommentService } from '../../services/comment.service';
import { NotificationService } from '../../services/notification.service';
import { User } from '../../models/user.model';
import { getCurrentUser } from '@aws-amplify/auth';
import { TaskService } from '../../services/task.service';
import { RichTextEditorComponent } from '../rich-text-editor/rich-text-editor';

@Component({
  selector: 'app-comments',
  standalone: true,
  imports: [CommonModule, FormsModule, RichTextEditorComponent],
  templateUrl: "./comments.html"
})
export class CommentsComponent implements OnChanges, OnInit {
  private commentService = inject(CommentService);
  private notificationService = inject(NotificationService);
  private taskService = inject(TaskService);

  @Input() taskId!: string;
  @Input() projectId!: string;
  @Input() employees: User[] = [];
  @Output() close = new EventEmitter<void>();

  comments: any[] = [];
  newCommentText = "";
  showMentions = false;
  filteredEmployees: User[] = [];
  mentionSearchTerm = '';
  cursorPosition = 0;
  selectedIndex = 0;
  mentionedUserIds: Set<string> = new Set();
  selectedFiles: File[] = [];

  @ViewChild('commentEditor') commentEditor!: RichTextEditorComponent;
  @ViewChild('commentsContainer') commentsContainer!: ElementRef<HTMLUListElement>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  ngOnInit() {
    if (this.taskId) this.loadComments();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['taskId'] && this.taskId) {
      await this.loadComments();
      this.resetForm();
      setTimeout(() => this.scrollToBottom(), 100);
    }
  }

  get isCommentEmpty(): boolean {
    if (!this.newCommentText) return true;
    const textOnly = this.newCommentText.replace(/<[^>]*>/g, '').trim();
    return textOnly.length === 0;
  }

  async addComment() {
    if (!this.taskId || this.isCommentEmpty) return;
    try {
      const user = await getCurrentUser();
      const task = await this.taskService.getTaskById(this.taskId);
      let imagePaths: string[] = [];
      if (this.selectedFiles.length > 0) {
        const tempId = crypto.randomUUID();
        imagePaths = await this.commentService.uploadCommentImages(this.selectedFiles, tempId);
      }
      await this.commentService.createComment(this.taskId, this.newCommentText, imagePaths);
      const sender = this.employees.find(e => e.cognitoId === user.userId);
      const senderName = sender ? `${sender.firstName} ${sender.name}` : 'A colleague';
      for (const recipientId of this.mentionedUserIds) {
        if (recipientId === user.userId) continue;
        await this.notificationService.sendNotification(recipientId, `${senderName} mentioned you on this task ${task?.nameTask}`, { taskId: this.taskId, projectId: this.projectId, senderName });
      }
      this.resetForm();
      await this.loadComments();
      this.notificationService.showToast('Comment sent', 'success');
      setTimeout(() => this.scrollToBottom(), 100);
    } catch (e) {
      this.notificationService.showToast('Error sending comment', 'error');
    }
  }

  async loadComments() {
    if (!this.taskId) return;
    try {
      const rawComments = await this.commentService.listComments(this.taskId);
      if (!rawComments || rawComments.length === 0) {
        this.comments = [];
        return;
      }
      const tempComments = rawComments.map(c => ({
        ...c,
        imageUrls: [],
        imagesLoaded: false
      })).sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      this.comments = tempComments;
      setTimeout(() => this.scrollToBottom(), 100);
    } catch (error) {
      console.error('loadComments crashed:', error);
    }
  }

  async toggleCommentImages(comment: any) {
    if (comment.imagesLoaded) {
      comment.imageUrls = [];
      comment.imagesLoaded = false;
      return;
    }
    if (!comment.imagePaths || comment.imagePaths.length === 0) return;
    const resolvedUrls: string[] = [];
    for (const path of comment.imagePaths) {
      if (path) {
        const url = await this.commentService.getCommentImageUrl(path);
        if (url) resolvedUrls.push(url);
      }
    }
    comment.imageUrls = resolvedUrls;
    comment.imagesLoaded = true;
  }

  private resetForm() {
    this.newCommentText = '';
    if (this.commentEditor) {
      this.commentEditor.writeValue('');
    }
    this.selectedFiles = [];
    if (this.fileInput) this.fileInput.nativeElement.value = '';
    this.showMentions = false;
    this.mentionedUserIds.clear();
    const span = document.getElementById('fileNames');
    if (span) span.textContent = 'No file';
  }

  // Utils mentions scroll
  onCommentInputChange() {
    if (!this.commentEditor) return;
    const { text } = this.commentEditor.getTextBeforeCursor();
    const match = text.match(/@(\w*)$/);
    if (match) {
      this.mentionSearchTerm = match[1].toLowerCase();
      // Archived users can't be mentioned (they no longer receive notifications),
      // but `employees` keeps them so past mentions still resolve to a name.
      this.filteredEmployees = this.employees.filter(emp =>
        !emp.isArchived && (
          emp.name.toLowerCase().includes(this.mentionSearchTerm) ||
          emp.firstName?.toLowerCase().includes(this.mentionSearchTerm)
        )
      );
      this.showMentions = this.filteredEmployees.length > 0;
    } else {
      this.showMentions = false;
    }
  }

  selectMention(employee: User) {
    const mentionText = `${employee.firstName} ${employee.name}`;
    if (this.commentEditor) {
      this.commentEditor.replaceWordBeforeCursor(/@\w*$/, `@${mentionText} `);
    }
    if (employee.cognitoId) this.mentionedUserIds.add(employee.cognitoId);
    this.showMentions = false;
  }

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.selectedFiles = [...this.selectedFiles, ...Array.from(input.files)];
      const span = document.getElementById('fileNames');
      if (span) span.textContent = `${this.selectedFiles.length} file(s) selected`;
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.showMentions) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex + 1) % this.filteredEmployees.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex - 1 + this.filteredEmployees.length) % this.filteredEmployees.length;
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      this.selectMention(this.filteredEmployees[this.selectedIndex]);
    }
  }

  scrollToBottom() {
    const el = this.commentsContainer?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  getEmployeeName(id: string | null | undefined): string {
    const emp = this.employees.find(e => e.cognitoId === id);
    return emp ? `${emp.firstName} ${emp.name}` : 'Unknown user';
  }

  getEmployeeInitials(id: string | null | undefined): string {
    const emp = this.employees.find(e => e.cognitoId === id);
    if (!emp) return '?';
    return `${emp.firstName?.charAt(0) || ''}${emp.name?.charAt(0) || ''}`.toUpperCase() || '?';
  }
}
